import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { authApi, AuthUser } from '../api/authApi'
import { apiClient } from '../lib/apiClient'
import { Organization, OrgMember, UserType } from '../types'

interface AuthContextValue {
  token: string | null
  user: AuthUser | null
  organization: Organization | null
  orgMember: OrgMember | null
  loading: boolean
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>
  signInWithMicrosoft: () => Promise<{ error: Error | null }>
  signInWithGoogle: () => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, displayName: string, orgName?: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  refreshOrg: () => Promise<void>
  completeOnboarding: () => Promise<void>
  resetOnboardingFlag: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [orgMember, setOrgMember] = useState<OrgMember | null>(null)
  const [loading, setLoading] = useState(true)

  const loadingFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const armLoadingFallback = (ms: number) => {
    if (loadingFallbackRef.current) clearTimeout(loadingFallbackRef.current)
    loadingFallbackRef.current = setTimeout(() => {
      console.warn('[Auth] Loading fallback fired — forcing loading=false')
      setLoading(false)
    }, ms)
  }

  const cancelLoadingFallback = () => {
    if (loadingFallbackRef.current) {
      clearTimeout(loadingFallbackRef.current)
      loadingFallbackRef.current = null
    }
  }

  const clearAuthState = () => {
    setToken(null)
    setUser(null)
    setOrganization(null)
    setOrgMember(null)
  }

  const refreshOrg = async () => {
    try {
      const me = await authApi.getMe()
      if (me) {
        setOrganization(me.organization)
        setOrgMember(me.orgMember)
      }
    } catch (err) {
      console.error('[Auth] Error refreshing org:', err)
    }
  }

  useEffect(() => {
    armLoadingFallback(8000)

    const storedToken = apiClient.getToken()
    if (!storedToken) {
      cancelLoadingFallback()
      setLoading(false)
      return
    }

    authApi.getMe()
      .then(me => {
        if (me) {
          setToken(storedToken)
          setUser(me.user)
          setOrganization(me.organization)
          setOrgMember(me.orgMember)
          if (me.user?.onboarding_completed) {
            try { localStorage.setItem('allocatiq_onboarding_completed', 'true') } catch { /* ignore */ }
          }
        } else {
          apiClient.clearToken()
          clearAuthState()
        }
      })
      .catch(err => {
        console.error('[Auth] Error restoring session:', err)
        apiClient.clearToken()
        clearAuthState()
      })
      .finally(() => {
        cancelLoadingFallback()
        setLoading(false)
      })

    return () => cancelLoadingFallback()
  }, [])

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await authApi.signIn(email, password)
      setToken(result.token)
      setUser(result.user)
      setOrganization(result.organization)
      setOrgMember(result.orgMember)
      return { error: null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  const signInWithMicrosoft = async () => {
    try {
      window.location.href = authApi.getMicrosoftOAuthUrl()
      return { error: null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  const signInWithGoogle = async () => {
    try {
      window.location.href = authApi.getGoogleOAuthUrl()
      return { error: null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  const signUp = async (email: string, password: string, displayName: string, orgName?: string) => {
    try {
      const result = await authApi.signUp(email, password, displayName, orgName)
      setToken(result.token)
      setUser(result.user)
      if (result.organization) setOrganization(result.organization)
      if (result.orgMember) setOrgMember(result.orgMember)
      return { error: null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  const signOut = async () => {
    try {
      await authApi.signOut()
    } finally {
      clearAuthState()
    }
  }

  const completeOnboarding = async () => {
    setUser(prev => prev ? { ...prev, onboarding_completed: true } : prev)
    try {
      localStorage.setItem('allocatiq_onboarding_completed', 'true')
    } catch { /* ignore */ }
    await authApi.completeOnboarding()
  }

  const resetOnboardingFlag = () => {
    setUser(prev => prev ? { ...prev, onboarding_completed: false } : prev)
    try {
      localStorage.removeItem('allocatiq_onboarding_completed')
    } catch { /* ignore */ }
  }

  return (
    <AuthContext.Provider value={{
      token,
      user,
      organization,
      orgMember,
      loading,
      signInWithEmail,
      signInWithMicrosoft,
      signInWithGoogle,
      signUp,
      signOut,
      refreshOrg,
      completeOnboarding,
      resetOnboardingFlag,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
